import {CommonModule} from '@angular/common';
import {Component, inject, OnDestroy, OnInit, DestroyRef, Input} from '@angular/core';
import {
    DxChartModule,
    DxPieChartModule
} from 'devextreme-angular';
import {
    AttendanceSummaryDto,
    AuthService,
    ExportScoreQuery,
    GetAttendanceScoreQuery,
    GetAttendanceSummaryQuery, GetAttendanceTrendingQuery, IMyScoreShiftQuery, ITrendingQuery,
    MyScoreClient,
    PermissionType,
    PunchInStatus
} from "@services";
import {CardItem, SummaryCardComponent} from "./summary-card/summary-card.component";
import {catchError, distinctUntilChanged, switchMap, map, tap, shareReplay} from "rxjs/operators";
import {EMPTY, BehaviorSubject, combineLatest, forkJoin, Observable, of} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ScoreDashboardComponent} from "../../../components/library/score-dashboard/score-dashboard.component";
import {ChartDataPoint} from "../../../components/library/doughnut-chart/doughnut-chart.component";

export const attendanceTimeOffTypes = ['Vacation', 'Floater Day', 'Leave of Absence', 'Agency Time Off', 'Lieu Day'];

export function attendancePalette(): Record<string, string> {
    const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();
    return {
        [PunchInStatus.OnTime]: getColor('--bs-success'),
        [PunchInStatus.ArrivedLate]: getColor('--bs-warning'),
        [PunchInStatus.Exception]: getColor('--bs-danger'),
        [PunchInStatus.Absent]: getColor('--bs-secondary'),
        ['Vacation']: 'rgba(0, 165, 217, 0.2)',
        ['Lieu Day']: 'rgba(0, 165, 217, 0.6)',
        ['Floater Day']: 'rgba(0, 165, 217, 0.8)',
        ['Leave of Absence']: 'rgba(0, 165, 217, 1)',
        ['Agency Time Off']: 'rgba(0, 165, 217, 0.4)',
    };
}

@Component({
    selector: 'app-my-score',
    imports: [CommonModule, DxPieChartModule, DxChartModule, SummaryCardComponent, ScoreDashboardComponent],
    templateUrl: './my-score.component.html',
    styleUrl: './my-score.component.scss',
})
export class MyScoreComponent implements OnInit, OnDestroy {
    private myScoreClient = inject(MyScoreClient)
    private destroyRef = inject(DestroyRef);
    protected authService = inject(AuthService);

    @Input() employeeId: string | null;
    @Input() daysLookBack: number = 0;
    @Input() containerClass: string = 'p-4 h-100 overflow-auto';

    summaryCards: CardItem[] = [];
    financialDetails: CardItem[] = [];

    private readonly dateRange$ = new BehaviorSubject<[Date, Date]>([new Date(), new Date()]);
    summary$: Observable<any>;

    palette: Record<string, string> = {};
    protected readonly attendanceTimeOffTypes = attendanceTimeOffTypes;

    loadScoreData = (query: IMyScoreShiftQuery) => {
        return this.myScoreClient.getAttendanceScore(new GetAttendanceScoreQuery(query));
    }

    loadTrendingData = (query: ITrendingQuery) => {
        const userId = this.sectionUserId(PermissionType.MyScoreTrending);
        return this.myScoreClient.getTrending(new GetAttendanceTrendingQuery({...query, userId}));
    }

    processScoreData = (items: ChartDataPoint[]) => {
        const filteredItems = items.filter(i => !attendanceTimeOffTypes.includes(i.argument));
        const timeOffSum = items
            .filter(i => attendanceTimeOffTypes.includes(i.argument))
            .reduce((sum, current) => sum + current.value, 0);

        if (timeOffSum > 0) {
            filteredItems.push({argument: 'Time Off', value: timeOffSum} as ChartDataPoint);
        }
        return filteredItems;
    }

    ngOnInit(): void {
        this.palette = attendancePalette();

        this.summary$ = combineLatest([this.dateRange$]).pipe(
            map(([range]) => ({from: range?.[0], to: range?.[1]})),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            switchMap(({from, to}) => {
                if (from) {
                    return this.loadSummaries(from, to).pipe(
                        catchError(() => this.empty())
                    );
                }
                return this.empty();
            }),
            tap(([attendance, financial]) => {
                if (attendance) {
                    this.summaryCards = [
                        {
                            label: 'On Site',
                            subLabel: '(On-time + Late)',
                            value: attendance.onSite,
                            icon: 'fas fa-user-check',
                            color: 'text-success',
                            bg: 'bg-success-subtle'
                        },
                        {
                            label: 'Expected',
                            subLabel: '(On-site + Absent)',
                            value: attendance.expected,
                            icon: 'fas fa-user',
                            color: 'text-warning',
                            bg: 'bg-warning-subtle'
                        },
                        {
                            label: 'Time Off',
                            subLabel: '',
                            value: attendance.timeOff,
                            icon: 'fas fa-user-times',
                            color: 'text-info',
                            bg: 'bg-info-subtle'
                        },
                    ];
                }

                if (financial) {
                    this.financialDetails = [
                        {
                            label: 'Hours Worked',
                            value: Math.round(financial.hoursWorked),
                            icon: 'fas fa-clock',
                            color: 'text-warning',
                            bg: 'bg-warning-subtle'
                        },
                        {
                            label: 'Labour Cost',
                            value: financial.labourCost,
                            currency: true,
                            icon: 'fas fa-dollar-sign',
                            color: 'text-success',
                            bg: 'bg-success-subtle'
                        },
                        {
                            label: 'Meal Tickets',
                            value: financial.mealTickets,
                            icon: 'fas fa-utensils',
                            color: 'text-info',
                            bg: 'bg-info-subtle'
                        },
                    ];
                }
            }),
            shareReplay(1),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    ngOnDestroy(): void {
        this.dateRange$.complete();
    }

    empty() {
        this.summaryCards = this.summaryCards.map(card => ({...card, value: 0}));
        this.financialDetails = this.financialDetails.map(card => ({...card, value: 0}));
        return EMPTY;
    }

    exportData = (from: Date, to: Date, userId: string | null) => {
        const query = new ExportScoreQuery({from, to, userId});
        return this.myScoreClient.exportScore(query);
    }

    onDateRangeChanged(range: [Date, Date]) {
        this.dateRange$.next(range);
    }

    protected canView(permission: PermissionType): boolean {
        return this.authService.canViewMyScoreSection(this.sectionUserId(permission), permission);
    }

    // Without an employee (the My Score page) a section shows all employees when the user may see them, otherwise their own data.
    protected sectionUserId(permission: PermissionType): string | null {
        if (this.employeeId)
            return this.employeeId;

        return this.authService.canViewMyScoreSection(null, permission) ? null : this.authService.userId;
    }

    // Financial details come from the same summary, so a second call is only needed when they cover different employees.
    private loadSummaries(from: Date, to: Date): Observable<[AttendanceSummaryDto | null, AttendanceSummaryDto | null]> {
        const load = (userId: string | null) =>
            this.myScoreClient.getAttendanceSummary(new GetAttendanceSummaryQuery({from, to, userId}));

        const showAttendance = this.canView(PermissionType.MyScoreAttendance);
        const attendanceUserId = this.sectionUserId(PermissionType.MyScoreAttendance);
        const financialUserId = this.sectionUserId(PermissionType.MyScoreFinancialDetails);

        const attendance$ = showAttendance ? load(attendanceUserId).pipe(shareReplay(1)) : of(null);
        let financial$: Observable<AttendanceSummaryDto | null> = of(null);
        if (this.canView(PermissionType.MyScoreFinancialDetails)) {
            financial$ = showAttendance && financialUserId === attendanceUserId ? attendance$ : load(financialUserId);
        }

        return forkJoin([attendance$, financial$]);
    }

    protected readonly PermissionType = PermissionType;
}
